import {
  Job,
  type UpdateJobCommand,
  type CreateJobIntakeCommand,
} from "@domain/model/Job";
import { asDate, isoOrNull } from "@domain/model/Time";
import { asJobId, asConstructionSiteId, asPersonId } from "@domain/model/Ids";
import type {
  JobDto,
  JobUpdateRequestDto,
  AddressRequestDto,
  JobIntakeRequestDto,
} from "../dto/JobDto";

export const jobFromDto = (d: JobDto): Job => ({
  id: asJobId(d.id),
  title: d.title,
  constructionSiteId: asConstructionSiteId(d.constructionSiteId),
  description: d.description,
  notes: d.notes,
  plannedStartAt: d.plannedStartAt ? asDate(d.plannedStartAt) : null,
  assignedPersonId: d.assignedPersonId ? asPersonId(d.assignedPersonId) : null,
  jobState: d.jobState,
  lastEventAt: d.lastEventAt ? asDate(d.lastEventAt) : null,
  version: d.version,
  createdAt: asDate(d.createdAt),
  updatedAt: asDate(d.updatedAt),
  deletedAt: d.deletedAt ? asDate(d.deletedAt) : null,
});

export const jobUpdateRequestFromCommand = (
  cmd: UpdateJobCommand,
): JobUpdateRequestDto => ({
  title: cmd.title ?? null,
  constructionSiteId: cmd.constructionSiteId
    ? String(cmd.constructionSiteId)
    : null,
  description: cmd.description ?? null,
  notes: cmd.notes ?? null,
  plannedStartAt:
    cmd.plannedStartAt === undefined ? null : isoOrNull(cmd.plannedStartAt),
  assignedPersonId: cmd.assignedPersonId ? String(cmd.assignedPersonId) : null,
});

export const addressRequestFromCommand = (address: {
  label?: string | null;
  street: string;
  streetNumber: string;
  zip: string;
  city: string;
  country: string;
}): AddressRequestDto => ({
  label: address.label ?? null,
  street: address.street,
  streetNumber: address.streetNumber,
  zip: address.zip,
  city: address.city,
  country: address.country,
});

export const jobIntakeRequestFromCommand = (
  cmd: CreateJobIntakeCommand,
): JobIntakeRequestDto => ({
  job: {
    title: cmd.job.title,
    description: cmd.job.description ?? null,
    notes: cmd.job.notes ?? null,
    plannedStartAt: isoOrNull(cmd.job.plannedStartAt),
    assignedPersonId: cmd.job.assignedPersonId
      ? String(cmd.job.assignedPersonId)
      : null,
  },
  client:
    cmd.client.kind === "existing"
      ? {
          kind: "existing",
          clientId: String(cmd.client.clientId),
        }
      : {
          kind: "new",
          name: cmd.client.name,
          contactName: cmd.client.contactName ?? null,
          phone: cmd.client.phone ?? null,
          email: cmd.client.email ?? null,
          address: addressRequestFromCommand(cmd.client.address),
        },
  constructionSite:
    cmd.constructionSite.kind === "existing"
      ? {
          kind: "existing",
          constructionSiteId: String(cmd.constructionSite.constructionSiteId),
        }
      : cmd.constructionSite.kind === "client_address"
        ? {
            kind: "client_address",
            label: cmd.constructionSite.label ?? null,
          }
        : {
            kind: "new_address",
            label: cmd.constructionSite.label ?? null,
            address: addressRequestFromCommand(cmd.constructionSite.address),
          },
});
